/**
 * Delete Confirmation Modal Component
 * 
 * Modal dialog for confirming base map deletion with name verification.
 * Requirements: 30.1, 30.2, 30.3, 30.4, 30.5, 30.6, 30.7
 */

import React, { useState } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Alert,
  FormField,
  Input
} from '@cloudscape-design/components';

interface DeleteConfirmationModalProps {
  visible: boolean;
  baseMapName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  visible,
  baseMapName,
  onConfirm,
  onCancel
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
      setConfirmationText('');
    }
  };

  const handleCancel = () => {
    setConfirmationText('');
    onCancel();
  };

  const isConfirmationValid = confirmationText === baseMapName;

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header="Delete Base Map"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!isConfirmationValid || isDeleting}
              loading={isDeleting}
            >
              Delete
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning" header="This action cannot be undone">
          <SpaceBetween size="xs">
            <Box variant="p">
              You are about to permanently delete the base map <strong>{baseMapName}</strong>.
            </Box>
            <Box variant="p">
              <strong>Important:</strong> This will only remove the base map configuration from the database.
              S3 files (GeoTIFF images, thumbnails, tile indexes) will NOT be deleted automatically and must
              be removed manually if needed.
            </Box>
          </SpaceBetween>
        </Alert>

        <FormField
          label="Confirmation"
          description={`Type the base map name "${baseMapName}" to confirm deletion`}
        >
          <Input
            value={confirmationText}
            onChange={({ detail }) => setConfirmationText(detail.value)}
            placeholder={baseMapName}
            ariaRequired
          />
        </FormField>

        {confirmationText && !isConfirmationValid && (
          <Alert type="error">
            The name you entered does not match. Please type "{baseMapName}" exactly.
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default DeleteConfirmationModal;
